import { useState } from 'react';
import { exampleAds } from '../../../mocks/exampleAds';

function AvatarImage({ name, src }: { name: string; src: string }) {
  const [failed, setFailed] = useState(false);
  const initials = name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

  if (failed) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-amber-100 text-amber-700 text-[10px] font-bold">
        {initials}
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={name}
      className="w-full h-full object-cover object-top"
      onError={() => setFailed(true)}
    />
  );
}

const platformColors: Record<string, { badge: string; button: string; accent: string }> = {
  Facebook: {
    badge: 'text-blue-700 bg-blue-50',
    button: 'bg-blue-600 hover:bg-blue-700 text-white',
    accent: 'text-blue-600',
  },
  Instagram: {
    badge: 'text-rose-600 bg-rose-50',
    button: 'bg-gradient-to-r from-rose-500 to-orange-400 hover:from-rose-600 hover:to-orange-500 text-white',
    accent: 'text-rose-500',
  },
  Google: {
    badge: 'text-amber-700 bg-amber-50',
    button: 'bg-amber-500 hover:bg-amber-600 text-white',
    accent: 'text-amber-600',
  },
};

export default function ExampleAds() {
  const [activeIdx, setActiveIdx] = useState(0);
  const ad = exampleAds[activeIdx];
  const colors = platformColors[ad.platform];
  const isSquare = ad.platform === 'Instagram';

  return (
    <section id="examples" className="py-24 bg-stone-950">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-14">
          <span className="inline-block text-sm font-semibold text-orange-400 uppercase tracking-widest mb-3">
            Real Output
          </span>
          <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-4">
            See what AdForge actually
            <br />
            <span className="bg-gradient-to-r from-orange-400 to-amber-300 bg-clip-text text-transparent">
              generates for real businesses
            </span>
          </h2>
          <p className="text-lg text-white/60 max-w-xl mx-auto">
            AI copy + AI-generated creative images, ready in under 30 seconds. Pick a demo below.
          </p>
        </div>

        {/* Tab switcher */}
        <div className="flex justify-center gap-3 mb-12 flex-wrap">
          {exampleAds.map((item, idx) => (
            <button
              key={item.id}
              onClick={() => setActiveIdx(idx)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                activeIdx === idx
                  ? 'bg-gradient-to-r from-orange-500 to-amber-400 text-white'
                  : 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white border border-white/10'
              }`}
            >
              <i className={`${item.platformIcon} text-base`} />
              {item.platform} — {item.businessType}
            </button>
          ))}
        </div>

        {/* Ad preview */}
        <div className="max-w-[480px] mx-auto">
          <div className="bg-white rounded-2xl overflow-hidden">

            {/* Platform top bar */}
            <div className="bg-stone-50 px-4 py-3 flex items-center justify-between border-b border-stone-100">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full overflow-hidden bg-stone-200 flex-shrink-0">
                  <AvatarImage key={ad.id} src={ad.advertiserAvatar} name={ad.advertiserName} />
                </div>
                <div>
                  <div className="text-xs font-bold text-stone-800 leading-tight">{ad.advertiserName}</div>
                  <div className="flex items-center gap-1 mt-0.5">
                    <span className="text-[10px] text-stone-400">Sponsored</span>
                    <span className="text-[10px] text-stone-300">·</span>
                    <i className={`${ad.platformIcon} text-[10px] text-stone-400`} />
                    <span className="text-[10px] text-stone-400">{ad.platform}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${colors.badge}`}>
                  {ad.businessType}
                </span>
              </div>
            </div>

            {/* Hook text above image */}
            <div className="px-4 pt-3 pb-2">
              <p className="text-sm text-stone-700 leading-relaxed">
                <strong className="text-stone-900">{ad.hook}</strong>
              </p>
            </div>

            {/* AI-generated creative image */}
            <div
              className={`w-full overflow-hidden bg-stone-100 relative ${isSquare ? 'aspect-square' : 'aspect-[16/9]'}`}
            >
              <img
                src={ad.imageUrl}
                alt={`${ad.businessType} ad creative`}
                className="w-full h-full object-cover object-top"
              />
              {/* AI badge overlay */}
              <div className="absolute top-2.5 left-2.5">
                <span className="inline-flex items-center gap-1 bg-black/60 backdrop-blur-sm text-white text-[10px] font-semibold px-2 py-1 rounded-full">
                  <i className="ri-sparkling-fill text-orange-400 text-[10px]" />
                  AI Generated Image
                </span>
              </div>
            </div>

            {/* Copy section */}
            <div className="px-4 py-4 border-t border-stone-100">
              <p className="text-[13px] font-extrabold text-stone-900 leading-snug mb-1">
                {ad.headline}
              </p>
              <p className="text-[12px] text-stone-500 leading-relaxed mb-4 line-clamp-3">
                {ad.body}
              </p>
              {/* CTA Button */}
              <button
                type="button"
                className={`w-full text-sm font-bold py-2.5 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap ${colors.button}`}
              >
                {ad.cta}
              </button>
            </div>

            {/* Footer: tags */}
            <div className="px-4 pb-3 flex items-center gap-1.5 flex-wrap">
              {ad.imageTags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 bg-orange-50 text-orange-600 text-[10px] font-semibold px-2 py-0.5 rounded-full"
                >
                  <i className="ri-flashlight-fill text-[9px]" />
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* "Generated by" line + CTA */}
          <div className="mt-6 text-center space-y-3">
            <p className="text-xs text-white/40">
              This entire ad — copy + image — was generated in under 30 seconds
            </p>
            <a
              href="/dashboard"
              className="inline-flex items-center gap-2 text-sm font-semibold text-orange-400 hover:text-orange-300 transition-colors"
            >
              Generate ads like this for your business
              <i className="ri-arrow-right-line" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
